/**
 * A confirmation dialog for the one destructive action M2 has: deleting a
 * recipe.
 *
 * Undo would cover it — `recipe.delete` inverts to a `recipe.restore` carrying
 * the whole prior document — but the undo stack is in-memory, so a delete
 * followed by a reload is genuinely unrecoverable in M2. That asymmetry is what
 * earns the extra click.
 */

import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export interface ConfirmDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  readonly description: string;
  readonly confirmLabel?: string;
  readonly destructive?: boolean;
  /**
   * Hold the confirm button shut until the dialog's own question is answered.
   *
   * For the case where {@link ConfirmDialogProps.children} is not a *choice* but
   * a **required** one — "move these readings" cannot be confirmed until a
   * destination is picked, and a dialog that accepted the click and then failed
   * would be blaming the brewer for a question it asked badly.
   */
  readonly confirmDisabled?: boolean;
  readonly onConfirm: () => void;
  /**
   * Extra content between the description and the buttons.
   *
   * For the choice a confirmation sometimes carries alongside the yes/no —
   * "delete this batch" is one question, "and put its ingredients back" is
   * another, and folding the second into the prose would make it unanswerable.
   */
  readonly children?: ReactNode;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  destructive = false,
  confirmDisabled = false,
  onConfirm,
  children,
}: ConfirmDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {children}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant={destructive ? 'destructive' : 'default'}
            disabled={confirmDisabled}
            onClick={() => {
              onConfirm();
              onOpenChange(false);
            }}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
